import React, {useEffect} from 'react';

import SchemaFieldSelectorManager from 'models/managers/SchemaFieldSelectorManager';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';

import SchemaFieldSelectorStore from './SchemaFieldSelectorStore';

interface SelectorStoreProps {
  schemaFieldSelector: SchemaFieldSelector | null;
}

interface RequiredProps {
  registryId?: string;
}

/**
 * Wrapper component to render a component with a given selector loaded.
 *
 * When the selector is not loaded, it will
 *   - load all selectors for a registry if a registryId is given
 *   - otherwise load all selectors across all registries
 */
export default function withSelectorLoading<T extends RequiredProps & SelectorStoreProps>(
  WrappedComponent: React.ComponentType<T>,
  propsToSelectorId: (params: T) => string | null
): React.ComponentClass<Omit<T, 'schemaFieldSelector'>> {
  function mapSchemaFieldSelectorStoreToProps(
    schemaFieldSelectorStore: typeof SchemaFieldSelectorStore,
    props: T
  ): SelectorStoreProps {
    const schemaFieldSelectorId = propsToSelectorId(props);
    if (schemaFieldSelectorId) {
      return {schemaFieldSelector: schemaFieldSelectorStore.getSelectorById(schemaFieldSelectorId)};
    }
    return {schemaFieldSelector: null};
  }

  function WithSelectorComponent(props: T): JSX.Element {
    useEffect(() => {
      if (props.registryId) {
        SchemaFieldSelectorManager.fetchForRegistry(props.registryId);
      } else {
        SchemaFieldSelectorManager.fetchForAllRegistries();
      }
    }, [props.registryId]);
    return <WrappedComponent {...props} />;
  }

  return SchemaFieldSelectorStore.connect<Omit<T, 'schemaFieldSelector'>, SelectorStoreProps>(
    mapSchemaFieldSelectorStoreToProps
  )(WithSelectorComponent);
}
